import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pb'
import { COLLECTIONS } from '@/types'
import type { TProvisionLog, TTenantProvision } from '@/types'
import { usePbSubscription } from '@/lib/usePbSubscription'

export function useProvisionLogs(provisionId: string | null | undefined) {
  const queryKey = ['provision_logs', provisionId ?? '']
  usePbSubscription(COLLECTIONS.PROVISION_LOGS, queryKey)
  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!provisionId) return []
      const records = await pb.collection(COLLECTIONS.PROVISION_LOGS).getFullList<TProvisionLog>({
        filter: `provision = "${provisionId}"`,
      })
      return records.sort((a, b) => new Date((a.timestamp || '').replace(' ', 'T')).getTime() - new Date((b.timestamp || '').replace(' ', 'T')).getTime())
    },
    enabled: !!provisionId,
    refetchInterval: 3_000,
  })
}

export function useProvisionLogsByTenant(tenantId: string | null | undefined) {
  const queryKey = ['provision_logs_tenant', tenantId ?? '']
  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!tenantId) return []
      // Find the latest provision record for this tenant
      // NOTE: PB 0.36 can't combine filter+sort, so fetch all and sort client-side
      const provisions = await pb.collection(COLLECTIONS.TENANT_PROVISIONS).getFullList({
        filter: `tenant = "${tenantId}"`,
      })
      if (provisions.length === 0) return []
      // Sort by id descending (PB IDs are time-sortable) to get the newest
      provisions.sort((a, b) => b.id.localeCompare(a.id))
      // Prefer a running provision (active teardown/redeploy), otherwise latest
      const running = provisions.find(p => (p as any).status === 'running')
      const latestProvisionId = running ? running.id : provisions[0].id
      // Get logs for only the latest provision
      const records = await pb.collection(COLLECTIONS.PROVISION_LOGS).getFullList<TProvisionLog>({
        filter: `provision = "${latestProvisionId}"`,
      })
      return records.sort((a, b) => new Date((a.timestamp || '').replace(' ', 'T')).getTime() - new Date((b.timestamp || '').replace(' ', 'T')).getTime())
    },
    enabled: !!tenantId,
    refetchInterval: 2_000,
  })
}

/**
 * Fetch provision logs by job ID.
 * Looks up the provision record linked to the job, then fetches its logs.
 * This is the cleanest way to connect UI → API response (job_id) → logs.
 */
export function useProvisionLogsByJob(jobId: string | null | undefined) {
  // Step 1: Find the provision for this job
  const { data: provisionResult } = useQuery({
    queryKey: ['provision_by_job', jobId ?? ''],
    queryFn: async () => {
      if (!jobId) return null
      const result = await pb.collection(COLLECTIONS.TENANT_PROVISIONS).getList<TTenantProvision>(1, 1, {
        filter: `job = "${jobId}"`,
      })
      return result.items[0] ?? null
    },
    enabled: !!jobId,
    refetchInterval: 2_000,
  })

  const provisionId = provisionResult?.id ?? null

  // Step 2: Get logs for that provision
  const queryKey = ['provision_logs', provisionId ?? '']
  usePbSubscription(COLLECTIONS.PROVISION_LOGS, queryKey)
  const { data: logs = [], ...rest } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!provisionId) return []
      const records = await pb.collection(COLLECTIONS.PROVISION_LOGS).getFullList<TProvisionLog>({
        filter: `provision = "${provisionId}"`,
      })
      return records.sort((a, b) => new Date((a.timestamp || '').replace(' ', 'T')).getTime() - new Date((b.timestamp || '').replace(' ', 'T')).getTime())
    },
    enabled: !!provisionId,
    refetchInterval: 3_000,
  })

  return { data: logs, provisionId, provision: provisionResult, ...rest }
}
